import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { RevisionOption } from "./types";

interface RevisionSelectorProps {
  revisionOptions: RevisionOption[];
  selectedRevision: RevisionOption | null;
  isRevisionDropdownOpen: boolean;
  setIsRevisionDropdownOpen: (isOpen: boolean) => void;
  handleRevisionSelect: (revision: RevisionOption) => void;
  getPluralizedLabel?: (baseKey: string, count: number | undefined) => string;
}

export const RevisionSelector: React.FC<RevisionSelectorProps> = ({
  revisionOptions,
  selectedRevision,
  isRevisionDropdownOpen,
  setIsRevisionDropdownOpen,
  handleRevisionSelect,
}) => {
  const { t } = useTranslation();

  // Helper function to handle pluralization
  const getPluralizedLabel = (baseKey: string, count: number | undefined) => {
    if (count === undefined) return "";
    return count === 1
      ? t(`documents.badges.${baseKey}_one`)
      : t(`documents.badges.${baseKey}_other`);
  };

  // Create a ref for the dropdown container
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside of dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        isRevisionDropdownOpen
      ) {
        setIsRevisionDropdownOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isRevisionDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isRevisionDropdownOpen, setIsRevisionDropdownOpen]);

  return (
    <div className="mb-6">
      <div className="flex items-center mb-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          className="w-5 h-5 mr-2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>

        <span className="text-sm font-medium text-gray-700">
          {t("documents.revision.choose")}:
        </span>
      </div>
      <div className="relative mt-1" ref={dropdownRef}>
        <button
          type="button"
          className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
          onClick={() => setIsRevisionDropdownOpen(!isRevisionDropdownOpen)}
        >
          <div className="flex justify-between items-center">
            <span className="block truncate font-medium max-w-[75%]">
              {selectedRevision?.label || "Select a revision"}
            </span>
            <div className="flex items-center space-x-2">
              <span className="bg-gray-100 px-2 py-1 text-xs rounded-full">
                {selectedRevision?.documentTypes}{" "}
                {t("documents.badges.file_short")}
              </span>
              <span className="bg-gray-100 px-2 py-1 text-xs rounded-full">
                {selectedRevision?.languages}{" "}
                {getPluralizedLabel("language", selectedRevision?.languages)}
              </span>
            </div>
          </div>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </button>

        {isRevisionDropdownOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {revisionOptions.map((revision, index) => (
              <div
                key={index}
                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                onClick={() => handleRevisionSelect(revision)}
              >
                <div className="flex justify-between items-center">
                  <span
                    className={`block truncate max-w-[75%] ${
                      selectedRevision?.version === revision.version
                        ? "font-medium"
                        : "font-normal"
                    }`}
                  >
                    {revision.label}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="bg-gray-100 px-2 py-1 text-xs rounded-full">
                      {revision.documentTypes}{" "}
                      {t("documents.badges.file_short")}
                    </span>
                    <span className="bg-gray-100 px-2 py-1 text-xs rounded-full">
                      {revision.languages}{" "}
                      {getPluralizedLabel("language", revision.languages)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const RevisionInfo: React.FC<{
  selectedRevision: RevisionOption | null;
}> = ({ selectedRevision }) => {
  const { t } = useTranslation();

  // Helper function to handle pluralization
  const getPluralizedLabel = (baseKey: string, count: number | undefined) => {
    if (count === undefined) return "";
    return count === 1
      ? t(`documents.badges.${baseKey}_one`)
      : t(`documents.badges.${baseKey}_other`);
  };

  if (!selectedRevision) return null;

  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500 mb-6 border-b border-gray-200 pb-4">
      <div className="w-full break-words sm:w-auto">
        {t("documents.revision.name")}:{" "}
        <span className="font-medium text-gray-900">
          {selectedRevision.label}
        </span>
      </div>
      <div>
        {t("documents.revision.created")}:{" "}
        <span className="font-medium text-gray-900">
          {selectedRevision.date}
        </span>
      </div>
      <div>
        {selectedRevision.documentTypes}{" "}
        {getPluralizedLabel("file", selectedRevision.documentTypes)}
      </div>
      <div>
        {selectedRevision.languages}{" "}
        {getPluralizedLabel("language", selectedRevision.languages)}
      </div>
    </div>
  );
};
